import { useEffect, useRef, useState } from "react";

// Why pure Web Audio (no HTMLAudioElement): the Meta Quest Browser crashes its
// whole process (NPE in MediaSessionImpl.mediaSessionStateChanged) whenever an
// HTMLMediaElement playback state change fires while the installed TWA's WebXR
// session is live — Chromium tries to delegate the media-control notification
// to the host app and dies. Confirmed on-device via adb in every variant:
// with notification delegation disabled AND enabled, and with the element
// routed through a MediaElementAudioSourceNode (the element still registers a
// media session underneath). AudioBufferSourceNode playback never registers a
// media session — it's also how the slash SFX (useSlashSound.ts) always
// worked, and those never crashed. The element-based implementation this
// replaces lives in git history if the browser bug ever gets fixed.

let sharedContext: AudioContext | null = null;
const preloadedBuffers = new Map<string, AudioBuffer>();
const preloadPromises = new Map<string, Promise<AudioBuffer>>();

function getSharedContext(): AudioContext | null {
  if (!sharedContext) {
    try {
      sharedContext = new AudioContext();
    } catch {
      return null;
    }
  }
  return sharedContext;
}

function resumeSharedContext() {
  const ctx = getSharedContext();
  if (ctx?.state === "suspended") {
    void ctx.resume().catch(() => undefined);
  }
}

async function fetchAndDecodeTrack(src: string): Promise<AudioBuffer> {
  const ctx = getSharedContext();
  if (!ctx) throw new Error("Web Audio is not supported.");

  const response = await fetch(src);
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${src}`);
  const data = await response.arrayBuffer();
  return ctx.decodeAudioData(data);
}

export function getPreloadedTrack(src: string): AudioBuffer | null {
  return preloadedBuffers.get(src) ?? null;
}

export function preloadTrack(src: string): Promise<AudioBuffer> {
  const cached = preloadedBuffers.get(src);
  if (cached) return Promise.resolve(cached);

  const pending = preloadPromises.get(src);
  if (pending) return pending;

  const promise = fetchAndDecodeTrack(src)
    .then((buffer) => {
      preloadedBuffers.set(src, buffer);
      preloadPromises.delete(src);
      return buffer;
    })
    .catch((error) => {
      preloadPromises.delete(src);
      throw error;
    });
  preloadPromises.set(src, promise);
  return promise;
}

export async function preloadTracksSequentially(
  sources: string[],
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  let loaded = 0;
  onProgress?.(loaded, sources.length);
  for (const src of sources) {
    if (signal?.aborted)
      throw new DOMException("Preload aborted.", "AbortError");
    await preloadTrack(src);
    if (signal?.aborted)
      throw new DOMException("Preload aborted.", "AbortError");
    loaded += 1;
    onProgress?.(loaded, sources.length);
  }
}

export function releasePreloadedTracks(sources: string[]) {
  for (const src of sources) {
    preloadedBuffers.delete(src);
    preloadPromises.delete(src);
  }
}

// Kept as the app-wide "audio unlock on user gesture" entry point (menu/retry
// button handlers call this). With buffer-based playback there are no elements
// left to unlock — resuming the shared context is the whole job.
export function unlockRegisteredTracks() {
  resumeSharedContext();
}

export function useTrack(
  src: string | null,
  options?: { loop?: boolean; volume?: number; fallbackOnPlayError?: boolean },
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioFailed, setAudioFailed] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const loop = options?.loop ?? false;
  const volume = options?.volume ?? 1;

  const bufferRef = useRef<AudioBuffer | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  // Logical clock: `anchor` is the base-time at which track-time was zero.
  // While the AudioContext is running we anchor on ctx.currentTime (the audio
  // hardware clock, same source the old element clock derived from); if the
  // context is unavailable/suspended for a whole run we anchor on
  // performance.now()/1000 instead. `clockBase` records which base a run uses
  // so a run never mixes bases (no mid-run jumps).
  const anchorRef = useRef(0);
  const clockBaseRef = useRef<"ctx" | "wall">("wall");
  const pausedAtRef = useRef<number | null>(null);
  const playTokenRef = useRef(0);
  // play() may run before the decode finishes (cold start); the decode
  // completion handler starts the source late at the right offset.
  const pendingStartRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const now = () => {
    if (clockBaseRef.current === "ctx") {
      const ctx = getSharedContext();
      if (ctx) return ctx.currentTime;
    }
    return performance.now() / 1000;
  };

  const stopSource = () => {
    const source = sourceRef.current;
    sourceRef.current = null;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // stop() throws if the source never started; nothing to do.
      }
      source.disconnect();
    }
  };

  const startSource = (offsetSeconds: number, when?: number) => {
    const ctx = getSharedContext();
    const buffer = bufferRef.current;
    if (!ctx || !buffer) return false;
    if (ctx.state !== "running") return false;

    const startTime = Math.max(ctx.currentTime, when ?? ctx.currentTime);
    const previousSource = sourceRef.current;
    sourceRef.current = null;
    if (previousSource) {
      previousSource.onended = () => previousSource.disconnect();
      try {
        previousSource.stop(startTime);
      } catch {
        previousSource.disconnect();
      }
    }
    if (!gainRef.current) {
      gainRef.current = ctx.createGain();
      gainRef.current.connect(ctx.destination);
    }
    gainRef.current.gain.value = volume;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    source.connect(gainRef.current);
    if (!loop) {
      const token = playTokenRef.current;
      source.onended = () => {
        // Only a natural end counts — stopSource() clears onended first.
        if (playTokenRef.current === token && sourceRef.current === source) {
          sourceRef.current = null;
          setHasEnded(true);
        }
      };
    }
    source.start(
      startTime,
      loop ? offsetSeconds % buffer.duration : offsetSeconds,
    );
    sourceRef.current = source;
    return true;
  };

  useEffect(() => {
    if (!src) {
      stopSource();
      bufferRef.current = null;
      pausedAtRef.current = null;
      pendingStartRef.current = false;
      setIsPlaying(false);
      setIsReady(false);
      setAudioFailed(false);
      setHasEnded(false);
      return;
    }
    let cancelled = false;
    setAudioFailed(false);
    setHasEnded(false);
    setIsReady(false);

    const ctx = getSharedContext();
    if (!ctx) {
      setAudioFailed(true);
      return;
    }

    const cached = getPreloadedTrack(src);
    const loadPromise = cached
      ? Promise.resolve(cached)
      : (preloadPromises.get(src) ?? fetchAndDecodeTrack(src));

    loadPromise
      .then((buffer) => {
        if (cancelled) return;
        bufferRef.current = buffer;
        setIsReady(true);
        if (pendingStartRef.current) {
          pendingStartRef.current = false;
          // Join late at the elapsed offset so the clock play() anchored on
          // stays truthful — blocks were already scheduled against it.
          resumeSharedContext();
          const elapsed =
            pausedAtRef.current ?? Math.max(0, now() - anchorRef.current);
          if (pausedAtRef.current == null) {
            const scheduledTime = Math.max(
              getSharedContext()?.currentTime ?? 0,
              anchorRef.current,
            );
            startSource(elapsed, scheduledTime);
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        console.warn(`Audio track failed to load: ${src}`);
        setAudioFailed(true);
        setIsReady(false);
      });

    return () => {
      cancelled = true;
      pendingStartRef.current = false;
      bufferRef.current = null;
      setIsReady(false);
      setAudioFailed(false);
      setHasEnded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    return () => {
      pendingStartRef.current = false;
      stopSource();
      bufferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = (delaySeconds = 0) => {
    playTokenRef.current += 1;
    setHasEnded(false);
    resumeSharedContext();
    pausedAtRef.current = null;

    const ctx = getSharedContext();
    const canUseCtxClock = !!ctx && ctx.state === "running";
    clockBaseRef.current = canUseCtxClock ? "ctx" : "wall";
    anchorRef.current =
      canUseCtxClock && ctx
        ? ctx.currentTime + Math.max(0, delaySeconds)
        : now();

    if (bufferRef.current) {
      const started = startSource(0, anchorRef.current);
      if (!started && !audioFailed) pendingStartRef.current = true;
    } else if (!audioFailed) {
      pendingStartRef.current = true;
    }
    setIsPlaying(true);
  };

  const stop = () => {
    playTokenRef.current += 1;
    pendingStartRef.current = false;
    stopSource();
    pausedAtRef.current = null;
    setHasEnded(false);
    setIsPlaying(false);
  };

  // Pause/resume preserve the playback clock so blocks freeze in place rather
  // than jumping. isPlaying stays true throughout (same contract as the old
  // element-based implementation): getCurrentTime returns the frozen time
  // while paused, and resume shifts the anchor so elapsed time is seamless.
  const pause = () => {
    if (pausedAtRef.current != null) return;
    pausedAtRef.current = Math.max(0, now() - anchorRef.current);
    stopSource();
  };

  const resume = () => {
    if (pausedAtRef.current == null) return;
    resumeSharedContext();
    const offset = pausedAtRef.current;
    pausedAtRef.current = null;
    anchorRef.current = now() - offset;
    if (bufferRef.current) {
      startSource(offset);
    } else if (!audioFailed) {
      pendingStartRef.current = true;
    }
  };

  const getCurrentTime = (): number => {
    if (!isPlaying) return 0;
    if (pausedAtRef.current != null) return pausedAtRef.current;
    return Math.max(0, now() - anchorRef.current);
  };

  const getDuration = (): number => bufferRef.current?.duration ?? 0;

  return {
    play,
    stop,
    pause,
    resume,
    getCurrentTime,
    getDuration,
    isPlaying,
    isReady,
    hasEnded,
  };
}
